/**
 * Does an environment id name the machine this web app is already talking to?
 *
 * ── the failure this exists to make impossible ──────────────────────────────
 *
 * This desktop answers to TWO identifiers, and only one of them is the one the
 * SPA connects on:
 *
 *   - the primary Environment id, from `/.well-known/forge/environment` on the
 *     local Forge server, which is what `primaryServerConfig` is keyed by;
 *   - its CMMD device runtime id (`runtime_<uuid>`, minted once per install by
 *     `desktopInstallationIdentity` and published into the CMMD device
 *     registry), which is what the runtime inventory, the sidebar and the
 *     saved-environment registry call this machine.
 *
 * Anything that resolves a runtime connection by comparing against the primary
 * id alone reads the second form as a REMOTE runtime. It then looks for a relay
 * connection this machine will never open to itself, finds none, and reports
 * "nothing has answered yet" about the server it is currently connected to. On
 * `/new` that produced FOR-36439: Run on "Alexander's MacBook Pro", a local
 * server listing codex and claudeAgent, and a model picker saying "Models load
 * when this project starts. Send to start it."
 *
 * The thread route already had this seam. `findRemoteRuntimeThreadRouteTarget`
 * returns null, meaning "render locally", when the route's environment id is
 * the registered runtime id, which is why opening a local thread works and
 * starting one did not. This is that same rule, named, so the draft picker and
 * the submission resolver share one predicate instead of two approximations.
 */

/**
 * True when `environmentId` names this machine under either of its identities.
 *
 * `localDeviceRuntimeId` comes from {@link resolveLocalDeviceRuntimeId}, which
 * prefers what the connected server reports about itself over what this page
 * has in storage. Null means neither source knows a device runtime id, and then
 * only the primary id can answer. A null id must never match a null environment
 * id, or "no target selected" would resolve as "local".
 */
export function isLocalExecutionEnvironmentId(input: {
  readonly environmentId: string | null;
  readonly primaryEnvironmentId: string | null;
  readonly localDeviceRuntimeId: string | null;
}): boolean {
  if (input.environmentId === null) return false;
  if (input.environmentId === input.primaryEnvironmentId) return true;
  return input.environmentId === input.localDeviceRuntimeId;
}

/**
 * Which device runtime id names the machine the SPA is connected to.
 *
 * Two sources claim to answer this, and only one of them is trustworthy in an
 * ordinary browser:
 *
 *   - `serverReported` is `deviceRuntimeId` off the primary server's own
 *     environment descriptor. The desktop hands its installation identity to
 *     the server it launches, so this is the machine talking, about itself.
 *   - `stored` is `getStoredLocalDeviceRuntimeId()`, minted per browser profile
 *     under `forge.deviceRegistry.runtimeId`. It carries the desktop's id only
 *     when the page runs inside the desktop shell, where the bridge overwrites
 *     it. Opening the same local server at `http://127.0.0.1:3774/new` in Safari
 *     gives that page a different id it minted for itself.
 *
 * FOR-36439 shipped with only the second source, so the picker read the desktop
 * as a remote runtime, waited for a relay connection the machine will never open
 * to itself, and told the user to start the workspace it was already serving.
 *
 * The server wins when it answers. `stored` stays as the fallback for servers
 * that publish no id: pre-0.10.5 desktops under version skew, and dev checkouts
 * where the desktop bridge is still the only witness.
 */
export function resolveLocalDeviceRuntimeId(input: {
  readonly serverReported: string | null | undefined;
  readonly stored: string | null;
}): string | null {
  return input.serverReported ?? input.stored;
}
